import { TripAnnotationNoAudioReason } from '@backoffice-frontend/graphql'
import type { OverwatchMapTripAnnotationAudioUrlQuery } from '../AnnotationAudio.hook'

type TripAnnotationAudio =
  OverwatchMapTripAnnotationAudioUrlQuery['tripAnnotationAudioUrl']

/** Every reason that is final: asking again will not produce a URL. */
export type AnnotationAudioUnavailableReason =
  | TripAnnotationNoAudioReason.NotStored
  | TripAnnotationNoAudioReason.ClipMissing
  | TripAnnotationNoAudioReason.ForeignBucket
  | TripAnnotationNoAudioReason.UnreadableUri
  | TripAnnotationNoAudioReason.Unknown

/**
 * What the player shows. `processing` is the only transient "no audio" state:
 * a voicebot conversation's recording is still being built server-side, so the
 * hook asks again until it is ready or `exhausted` says it gave up polling.
 */
export type AnnotationAudioState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'processing'; exhausted: boolean }
  | { status: 'unavailable'; reason: AnnotationAudioUnavailableReason }
  | { status: 'error' }

/** How often a `PROCESSING` recording is asked for again; typically ready within ~10–20 s. */
export const PROCESSING_POLL_INTERVAL_MS = 5_000
/** Polls before giving up and offering a manual retry: ~1 minute at the interval above. */
export const PROCESSING_MAX_POLLS = 12

/**
 * A reason this build does not know yet (the schema can gain values before the
 * frontend ships) is treated as `UNKNOWN`, never as playable or as processing.
 */
const toUnavailableReason = (
  reason: string | null,
): AnnotationAudioUnavailableReason => {
  switch (reason) {
    case TripAnnotationNoAudioReason.NotStored:
    case TripAnnotationNoAudioReason.ClipMissing:
    case TripAnnotationNoAudioReason.ForeignBucket:
    case TripAnnotationNoAudioReason.UnreadableUri:
      return reason
    default:
      return TripAnnotationNoAudioReason.Unknown
  }
}

/** Maps one `tripAnnotationAudioUrl` answer to the player's state. */
export const toAnnotationAudioState = (
  audio: TripAnnotationAudio,
  pollsExhausted: boolean,
): AnnotationAudioState => {
  if (audio.url) return { status: 'ready', url: audio.url }
  if (audio.noAudioReason === TripAnnotationNoAudioReason.Processing) {
    return { status: 'processing', exhausted: pollsExhausted }
  }
  return {
    status: 'unavailable',
    reason: toUnavailableReason(audio.noAudioReason),
  }
}
